import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { SharedService } from '../../shared/shared.service';
import {
  CreateTaskStatusRequest,
  CreateTaskTypeGroupRequest,
  CreateTaskTypeRequest,
  TaskGroupOption,
  TaskStatusOption,
  TaskStatusResponse,
  TaskTypeGroupResponse,
  TaskTypeOption,
  TaskTypeResponse,
  UpdateTaskStatusRequest,
  UpdateTaskTypeGroupRequest,
  UpdateTaskTypeRequest,
} from '../models/task-type.models';

@Injectable({
  providedIn: 'root',
})
export class TaskTypeService {
  private baseUrl = `${environment.fastApiUrl}/api`;

  constructor(private sharedService: SharedService) {}

  // ==================== Task Type Groups ====================

  /**
   * Get all task type groups
   */
  getTaskTypeGroups(isActive?: boolean): Observable<TaskTypeGroupResponse[]> {
    let url = `${this.baseUrl}/task-type-groups`;
    if (isActive !== undefined) {
      url += `?is_active=${isActive}`;
    }
    return this.sharedService.getFastApiData(url);
  }

  /**
   * Get task type group by ID
   */
  getTaskTypeGroup(groupId: number): Observable<TaskTypeGroupResponse> {
    return this.sharedService.getFastApiData(`${this.baseUrl}/task-type-groups/${groupId}`);
  }

  /**
   * Create task type group
   */
  createTaskTypeGroup(request: CreateTaskTypeGroupRequest): Observable<any> {
    return this.sharedService
      .postFastApiDataWithHeaders(`${this.baseUrl}/task-type-groups`, request)
      .pipe(map((response) => response.body));
  }

  /**
   * Update task type group
   */
  updateTaskTypeGroup(groupId: number, request: UpdateTaskTypeGroupRequest): Observable<any> {
    return this.sharedService
      .putFastApiData(`${this.baseUrl}/task-type-groups/${groupId}`, request)
      .pipe(map((response) => response.body));
  }

  /**
   * Delete task type group
   */
  deleteTaskTypeGroup(groupId: number): Observable<any> {
    return this.sharedService
      .deleteFastApiData(`${this.baseUrl}/task-type-groups/${groupId}`)
      .pipe(map((response) => response.body));
  }

  // ==================== Task Types ====================

  /**
   * Get all task types
   */
  getTaskTypes(groupId?: number, isActive?: boolean): Observable<TaskTypeResponse[]> {
    let url = `${this.baseUrl}/task-types?`;
    const params: string[] = [];

    if (groupId) params.push(`group_id=${groupId}`);
    if (isActive !== undefined) params.push(`is_active=${isActive}`);

    url += params.join('&');
    return this.sharedService.getFastApiData(url);
  }

  /**
   * Create task type
   */
  createTaskType(request: CreateTaskTypeRequest): Observable<any> {
    return this.sharedService
      .postFastApiDataWithHeaders(`${this.baseUrl}/task-types`, request)
      .pipe(map((response) => response.body));
  }

  /**
   * Update task type
   */
  updateTaskType(typeId: number, request: UpdateTaskTypeRequest): Observable<any> {
    return this.sharedService
      .putFastApiData(`${this.baseUrl}/task-types/${typeId}`, request)
      .pipe(map((response) => response.body));
  }

  /**
   * Delete task type
   */
  deleteTaskType(typeId: number): Observable<any> {
    return this.sharedService
      .deleteFastApiData(`${this.baseUrl}/task-types/${typeId}`)
      .pipe(map((response) => response.body));
  }

  // ==================== Task Statuses ====================

  /**
   * Get all task statuses (global + group-specific)
   */
  getTaskStatuses(groupId?: number, isActive?: boolean): Observable<TaskStatusResponse[]> {
    let url = `${this.baseUrl}/task-statuses?`;
    const params: string[] = [];

    if (groupId) params.push(`group_id=${groupId}`);
    if (isActive !== undefined) params.push(`is_active=${isActive}`);

    url += params.join('&');
    return this.sharedService.getFastApiData(url);
  }

  /**
   * Get task status by ID
   */
  getTaskStatus(statusId: number): Observable<TaskStatusResponse> {
    return this.sharedService.getFastApiData(`${this.baseUrl}/task-statuses/${statusId}`);
  }

  /**
   * Create task status
   */
  createTaskStatus(request: CreateTaskStatusRequest): Observable<any> {
    return this.sharedService
      .postFastApiDataWithHeaders(`${this.baseUrl}/task-statuses`, request)
      .pipe(map((response) => response.body));
  }

  /**
   * Update task status
   */
  updateTaskStatus(statusId: number, request: UpdateTaskStatusRequest): Observable<any> {
    return this.sharedService
      .putFastApiData(`${this.baseUrl}/task-statuses/${statusId}`, request)
      .pipe(map((response) => response.body));
  }

  /**
   * Delete task status
   */
  deleteTaskStatus(statusId: number): Observable<any> {
    return this.sharedService
      .deleteFastApiData(`${this.baseUrl}/task-statuses/${statusId}`)
      .pipe(map((response) => response.body));
  }

  // ==================== Helper Methods for UI ====================

  /**
   * Convert task types to dropdown options
   */
  mapTaskTypesToOptions(taskTypes: TaskTypeResponse[]): TaskTypeOption[] {
    return taskTypes.map((type) => ({
      value: type.id,
      label: type.name,
      groupName: type.group?.name || '',
      defaultDueDays: type.default_due_days,
      defaultPriority: type.default_priority,
      requiresEntity: type.requires_linked_entity,
    }));
  }

  /**
   * Convert task statuses to dropdown options
   */
  mapStatusesToOptions(statuses: TaskStatusResponse[]): TaskStatusOption[] {
    return statuses.map((status) => ({
      value: status.id,
      label: status.name,
      code: status.code,
      isClosed: status.is_closed,
      color: status.color,
    }));
  }

  /**
   * Convert task groups to dropdown options
   */
  mapGroupsToOptions(groups: TaskTypeGroupResponse[]): TaskGroupOption[] {
    return groups.map((group) => ({
      value: group.id,
      label: group.name,
      code: group.code,
      color: group.color,
      icon: group.icon,
    }));
  }
}
